import moment from 'moment'
import { AxiosRequestConfig } from 'axios'
import { ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import _ from 'lodash'
import { IKnowledgeTestCorrectAnswerResponse } from '@/pages/KnowledgeTest/KnowledgeTest.interface'
export { default as classNames } from 'classnames'
import { knowledgeTestV1AnswerScore } from '@/constants/data-source/user-knowledge-test-v1.answer'
import { IRadioOption } from '@/components/ui/RadioGroupComponent/RadioGroupComponent.interface'
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Get key enum from value

export function getKeyByValue<
  TEnumKey extends string,
  TEnumVal extends string | number
>(myEnum: { [key in TEnumKey]: TEnumVal }, value: string): keyof typeof myEnum {
  const indexOfS = Object.values(myEnum).indexOf(value as TEnumVal)

  const key: any = Object.keys(myEnum)[indexOfS]

  return key
}

export function isEnumValue(value: any, enumeration: any): boolean {
  return Object.values(enumeration).includes(value)
}

interface NameMap {
  fullName: string
  shortName: string
}

export function convertToTitleCase(
  text: string,
  nameMaps: NameMap[] = []
): string {
  if (!text) {
    return ''
  }
  let result = text

  // Replace each fullName in the text with its shortName
  for (const { fullName, shortName } of nameMaps) {
    result = result.split(fullName).join(shortName)
  }

  // Convert the rest of the text to title case
  result = result
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

  return result
}

export function shortenIDString(input: string): string {
  const underscoreIndex = input.indexOf('_')
  if (underscoreIndex == -1) {
    return input // return the original string if no underscore is found
  }

  const prefix = input.substring(0, underscoreIndex + 1) // keep everything until and including the underscore
  const remaining = input.substring(underscoreIndex + 1)

  if (remaining.length > 6) {
    // if the part after the underscore is longer than 6 characters
    const middle = remaining.substring(0, 3) // keep the first 3 characters after the underscore
    const end = remaining.substring(remaining.length - 3) // keep the last 3 characters of the string
    return `${prefix}${middle}...${end}`
  } else {
    return input // return the original string if it's short enough
  }
}

export function displayDateBeautiful(
  date: moment.MomentInput,
  format = 'DD/MM/YY HH:mm'
) {
  return moment(date).format(format)
}

export const safeJsonParse = (json: string | undefined | null) => {
  try {
    return JSON.parse(json ?? '')
  } catch (e) {
    return null
  }
}

function filterQuestionKey(questionJson: IQuestionJson) {
  const regex = /^q_\d+$/
  const questionKeyList = Object.keys(questionJson).filter((key) =>
    regex.test(key)
  )

  return questionKeyList
}

function filterAnswerKey(answerJson: IAnswerJson) {
  const regex = /^a_\d+$/
  const questionKeyList = Object.keys(answerJson).filter((key) =>
    regex.test(key)
  )

  return questionKeyList
}

function randomItem(arr: string[], size: number) {
  // Shuffle the array using Lodash's `shuffle` and then take the first `size` elements
  return _.chain(arr)
    .shuffle() // Randomly shuffles the elements of the array
    .take(size) // Takes the first `size` elements from the shuffled array
    .value() // Resolves the chain value
}

// export function generateFormQuestion(prefixkey: string, i18nResource: ) {

// }

interface IQuestionJson {
  [qKey: string]: { [aKey: string]: string }
}

interface IAnswerJson {
  [aKey: string]: string
}
interface II18nSuitTestJson extends IQuestionJson {
  [qKey: string]: any
}
interface II18nKnowledgeTestJson {
  [groupKey: string]: IQuestionJson
}
export function generateSuiteTestQuestion(i18nResource: II18nSuitTestJson) {
  const objKey = 'suit_test'
  const questionKeyList = filterQuestionKey(i18nResource)
  const suiteTestQuestionList = questionKeyList.map((key) => {
    return `${objKey}.${key}`
  })

  return suiteTestQuestionList
}

export function generateKnowledgeTestQuestion(
  i18nResource: II18nKnowledgeTestJson
) {
  const objKey = 'knowledge_test'
  const knowledgeTestQuestionList: string[] = []

  for (const groupKey in i18nResource) {
    const randomCount = groupKey === 'cryptocurrency' ? 4 : 2
    const questionKeyList = filterQuestionKey(i18nResource[groupKey])
    const tempRandoms = randomItem(questionKeyList, randomCount)

    tempRandoms.forEach((questionKey) => {
      knowledgeTestQuestionList.push(`${objKey}.${groupKey}.${questionKey}`)
    })
  }

  return knowledgeTestQuestionList
}

export function generateKnowledgeTestReatakeQuestion(
  i18nResource: II18nKnowledgeTestJson,
  knowledgeTestCorrectAnswerResponse: IKnowledgeTestCorrectAnswerResponse[]
) {
  const exanFormData = knowledgeTestCorrectAnswerResponse.map(
    (correctAns): IExamFormData => {
      const ansLowercase = correctAns.A[0].correctAnswer.toLowerCase()
      const [category, subCategory, questionKey, correctAnswerKey] =
        ansLowercase.split('.')

      const resource = _.get(
        i18nResource,
        `${subCategory}.${questionKey}`
      ) as unknown as IAnswerJson
      const answerKeyList = filterAnswerKey(resource)

      const choiceList = answerKeyList.map<IRadioOption>((a) => ({
        label: _.get(
          i18nResource,
          `${subCategory}.${questionKey}.${a}`.toLocaleLowerCase()
        ) as unknown as string,
        value: `${category}.${subCategory}.${questionKey}.${a}`.toUpperCase(),
        // Display choice only correct answer
        disabled: correctAnswerKey === a ? false : true
      }))
      const regexFindExamNo = /\.Q_(\d+)\./
      const examNo = Number(correctAns.Q.match(regexFindExamNo)?.pop())

      return {
        examNo: examNo,
        questionTitleKey: `${category}.${subCategory}.${questionKey}.q`,
        correctAnswer: ansLowercase,
        choiceList
      }
    }
  )

  return exanFormData
}

export interface IExamFormData {
  examNo: number
  questionTitleKey: string
  correctAnswer?: string //? Stores the correct answer for a knowledge test or retake exam.
  choiceList: {
    label: any
    value: string
    isTextMark?: boolean //? Indicates whether this answer is the correct one for a knowledge test.
  }[]
}

export function generateExamFormData(
  i18nResource: any,
  type: 'suitTestData' | 'knowledgeTest'
): IExamFormData[] {
  const questions = getQuestionsByType(i18nResource, type)

  return questions.map((questionKey, index) => {
    const resource = _.get(i18nResource, questionKey)
    const answerKeys = filterAnswerKey(resource)

    const choices = createChoiceList(
      answerKeys,
      questionKey,
      i18nResource,
      type
    )

    return {
      examNo: index + 1,
      questionTitleKey: `${questionKey}.q`,
      choiceList: choices
    }
  })
}

function getQuestionsByType(i18nResource: any, type: string): string[] {
  switch (type) {
    case 'suitTestData':
      return generateSuiteTestQuestion(i18nResource['suit_test'])
    case 'knowledgeTest':
      return generateKnowledgeTestQuestion(i18nResource['knowledge_test'])
    default:
      throw new Error(`Not supported generateExamFormData for type ${type}`)
  }
}

function createChoiceList(
  answerKeys: string[],
  questionKey: string,
  i18nResource: any,
  type: 'suitTestData' | 'knowledgeTest'
): IRadioOption[] {
  const isKnowledgeTest = type === 'knowledgeTest'

  return answerKeys.map<IRadioOption>((answerKey) => {
    const isCorrectAnswer = Boolean(
      _.get(
        knowledgeTestV1AnswerScore,
        `${questionKey.toUpperCase()}.${answerKey.toUpperCase()}`
      )
    )

    return {
      label: _.get(i18nResource, `${questionKey}.${answerKey}`.toLowerCase()),
      value: `${questionKey}.${answerKey}`.toUpperCase(),
      ...(isKnowledgeTest && { isTextMark: isCorrectAnswer })
    }
  })
}

export * from './local'
export * from './cookie'
